/* Copy a skill from a public GitHub repo, branch or folder. A link to one skill saves
   it; a link holding several lists them to pick from. The copy is the workspace's own,
   taken at that commit: it doesn't follow upstream. */
import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Folder, Github, Loader2 } from "lucide-react";

import { TextInput } from "@/shared/components/settings-atoms";
import { BrandButton, GhostButton, IconButton } from "@/shared/components/ui-kit";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";

import { problemsOf } from "../folder";
import { useImportGithub } from "../hooks/useSkillWrites";
import { ProblemLines } from "./atoms";

import type { ApiError } from "@/shared/api/errors";
import type { SkillChoice } from "@/shared/types";

/** Why GitHub's skill wasn't copied: the reason, and the checker's problems when it got that far. */
function Refusal({ error, className = "" }: { error: ApiError; className?: string }) {
  return (
    <div role="alert" className={`rounded-[12px] bg-[var(--danger-bg)] ring-1 ring-[var(--danger-ring)] px-3 py-2.5 space-y-1 ${className}`}>
      <div className="text-[12.5px] leading-[1.5] text-[var(--danger)] font-medium">{error.message}</div>
      <ProblemLines problems={problemsOf(error)} withPath />
    </div>
  );
}

export default function GithubDialog({ onSaved, onClose }: {
  onSaved: (skillId: string) => void;
  onClose: () => void;
}) {
  const [url, setUrl] = useState("");
  const [choices, setChoices] = useState<SkillChoice[] | null>(null);
  const run = useImportGithub();
  const error = run.error as ApiError | null;

  const go = (folder?: string) =>
    run.mutate({ url: url.trim(), folder }, {
      onSuccess: (out) => ("skill" in out ? onSaved(out.skill.id) : setChoices(out.choices)),
    });

  return (
    <Dialog open onOpenChange={(open) => !open && !run.isPending && onClose()}>
      <DialogContent className="p-0 gap-0 w-[480px] max-w-[94vw] rounded-[16px] overflow-hidden bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog">
        <div className="px-5 pt-5 pb-3 pr-12 flex items-start gap-2">
          {choices && (
            <IconButton onClick={() => { setChoices(null); run.reset(); }} title="Back to the link" aria-label="Back to the link" className="-ml-1.5">
              <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2} />
            </IconButton>
          )}
          <div className="min-w-0">
            <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
              {choices ? `${choices.length} skills in this repo` : "Copy a skill from GitHub"}
            </DialogTitle>
            <DialogDescription className="text-[13px] leading-[1.5] text-[var(--text-tertiary)] mt-0.5">
              {choices
                ? "Pick the one to copy into the workspace."
                : "A public repo, a branch, or a folder in one. It's copied at that commit and doesn't follow later changes."}
            </DialogDescription>
          </div>
        </div>

        {choices ? (
          <div className="mx-5 mb-5 rounded-[12px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] overflow-hidden">
            <div className="max-h-[320px] overflow-y-auto divide-y divide-[var(--hairline)]">
              {choices.map((c) => (
                <button
                  key={c.folder}
                  type="button"
                  disabled={run.isPending}
                  onClick={() => go(c.folder)}
                  className="press w-full flex items-center gap-2.5 px-3 py-2.5 text-left min-w-0 hover:bg-[var(--accent-hover)] disabled:opacity-60"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium text-[var(--text-primary)] truncate">{c.name}</span>
                    <span className="block text-[12px] text-[var(--text-tertiary)] line-clamp-2 mt-[1px]">{c.description}</span>
                    <span className="mt-1 inline-flex items-center gap-1 font-mono text-[11px] text-[var(--text-quaternary)]">
                      <Folder className="w-3 h-3" strokeWidth={1.75} /> {c.folder}
                    </span>
                  </span>
                  {run.isPending && run.variables?.folder === c.folder
                    ? <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin text-[var(--text-tertiary)]" strokeWidth={2} />
                    : <ChevronRight className="w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)]" strokeWidth={2} />}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); if (url.trim()) go(); }} className="px-5 pb-5 space-y-3">
            <div className="relative">
              <Github className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-tertiary)]" strokeWidth={1.75} />
              <TextInput
                autoFocus
                value={url}
                onChange={(next) => { setUrl(next); if (run.isError) run.reset(); }}
                aria-label="GitHub link"
                placeholder="github.com/owner/repo/tree/main/skills/name"
                className="pl-8 font-mono text-[16px] sm:text-[12.5px]"
              />
            </div>
            {error && <Refusal error={error} />}
            <div className="flex items-center justify-end gap-2">
              <GhostButton type="button" onClick={onClose} disabled={run.isPending}>Cancel</GhostButton>
              <BrandButton type="submit" disabled={!url.trim() || run.isPending} className="disabled:opacity-40 disabled:pointer-events-none">
                {run.isPending && <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.25} />}
                {run.isPending ? "Reading the repo…" : "Copy skill"}
              </BrandButton>
            </div>
          </form>
        )}
        {choices && error && <Refusal error={error} className="mx-5 -mt-2 mb-5" />}
      </DialogContent>
    </Dialog>
  );
}
